import React, {useState, useEffect, useMemo, useCallback} from 'react'
import {useIntl} from 'react-intl'
import {KTCard, KTCardBody, Table, Loading} from '../../components'
import {CustomCell} from '../../components/Table/columns/CustomCell'
import {CustomHeader} from '../../components/Table/columns/CustomHeader'
import {CustomActionCell} from '../../components/Table/columns/CustomActionCell'
import {ListHeader} from '../../components/Table/header/ListHeader'
import {ListLoading} from '../../components/Table/ListLoading'
import {PageTitle} from '../../core'

import {useDispatch, useSelector} from 'react-redux'
import {
  notificationGetAll,
  notificationComplete,
  notificationExcel,
} from '../../redux/slices/notification.slice'

import {
  useMemoizedStaticData
} from '../../utils/performanceOptimizations'

const selectNotificationData = (state) => ({
  notification: state.notification.notification,
  complete: state.notification.complete,
  loading: state.notification.loading
})

function Notification() {
  const intl = useIntl()
  const {notification, complete, loading} = useSelector(selectNotificationData)
  const dispatch = useDispatch()

  const [params, setParams] = useState()

  useEffect(() => {
    if (params) dispatch(notificationGetAll(params))
  }, [params, complete, dispatch])

  const handleSearch = useCallback((item) => {
    setParams((prevState) => ({...prevState, name: item}))
  }, [])

  const handlePagination = useCallback((pagination) => {
    setParams((prevState) => ({...prevState, ...pagination}))
  }, [])

  const handleExcelExport = useCallback(() => {
    dispatch(notificationExcel(params, intl.formatMessage({id: 'BC.NOTIFICATION'})))
  }, [dispatch, params, intl])

  const handleFilterSubmit = useCallback((values) => {
    setParams({...values})
  }, [])

  const handleNotificationComplete = useCallback((id) => {
    dispatch(notificationComplete({id}))
  }, [dispatch])

  const columns = useMemo(() => [
    {
      Header: (props) => (
        <CustomHeader
          tableProps={props}
          title={intl.formatMessage({id: 'NOTIFICATION.SEARCH.CONTENT'})}
          className='min-w-125px'
        />
      ),
      accessor: 'content',
      Cell: ({...props}) => (
        <CustomCell item={props} to={`/${props.data[props.row.index].sourcePath}`} />
      ),
    },
    {
      Header: (props) => (
        <CustomHeader
          tableProps={props}
          title={intl.formatMessage({id: 'NOTIFICATION.SEARCH.NOTIFICATIONID'})}
          className='min-w-125px'
        />
      ),
      accessor: 'notificationTypeId',
      Cell: ({...props}) => (
        <CustomCell
          item={props}
          to={`/${props.data[props.row.index].sourcePath}`}
          notificationType
        />
      ),
    },
    {
      Header: (props) => (
        <CustomHeader
          tableProps={props}
          title={intl.formatMessage({id: 'NOTIFICATION.SEARCH.ISCOMPLETED'})}
          className='min-w-125px'
        />
      ),
      accessor: 'isCompleted',
      Cell: ({...props}) => (
        <CustomCell item={props} to={`/${props.data[props.row.index].sourcePath}`} />
      ),
    },
    {
      Header: () => <></>,
      id: 'actions',
      Cell: ({...props}) => (
        <CustomActionCell
          id={props.data[props.row.index].id}
          title={intl.formatMessage({id: 'NOTIFICATION.SEARCH.BUTTON'})}
          onClick={handleNotificationComplete}
        />
      ),
    },
  ], [intl, handleNotificationComplete])

  const actionButtons = useMemoizedStaticData(() => [
    {
      title: 'BUTTON.EXCEL',
      onClick: handleExcelExport,
      icon: '/images/icons/duotune/arrows/arr044.svg',
      variant: 'light-primary',
    },
    {
      title: 'FILTER',
      icon: '/images/icons/duotune/general/gen031.svg',
      variant: 'light-primary',
      list: [
        {
          title: 'NOTIFICATION.SEARCH.CONTENT',
          type: 'text',
          name: 'content',
        },
        {
          title: 'NOTIFICATION.SEARCH.ISCOMPLETED',
          data: [
            {
              id: 'true',
              name: intl.formatMessage({id: 'NOTIFICATION.SEARCH.ISCOMPLETED.TRUE'}),
            },
            {
              id: 'false',
              name: intl.formatMessage({id: 'NOTIFICATION.SEARCH.ISCOMPLETED.FALSE'}),
            },
          ],
          name: 'isCompleted',
        },
      ],
    },
  ], [handleExcelExport, intl])

  return (
    <>
      <PageTitle>{intl.formatMessage({id: 'BC.NOTIFICATION'})}</PageTitle>
      <KTCard>
        <ListHeader
          search={handleSearch}
          actionButtons={actionButtons}
          onSubmit={handleFilterSubmit}
          pages={'NOTIFICATION'}
        />
        <KTCardBody>
          {!notification && <ListLoading />}
          {notification && (
            <Table
              items={notification}
              cols={columns}
              handlePagination={handlePagination}
              paddless
            />
          )}
        </KTCardBody>
      </KTCard>

      {loading && <Loading />}
    </>
  )
}
export {Notification}
